/**
 * D-EZ-104 — THE ODDS OF A ROLL, FROM THE PRINTED RULE.
 *
 * Every number on a stakes card comes from here, and every function here is the
 * printed rule for one roll type counted over all of its faces — not an
 * estimate, not a simulation. Where the engine already owns a piece of the rule
 * (the defender slot, the brake pedal ladder, the chip contest) this module
 * CALLS the engine's own function per face rather than copying it, so the odds
 * cannot drift from what the dice will actually do. `odds.test.ts` checks each
 * one against brute-force enumeration of faces through the real resolvers.
 *
 * Exact rationals: every result carries `hits / of`, the count of faces that
 * succeed over the size of the face space. `p` is the same thing as a number.
 *
 * NARRATIVE ONLY. Nothing here consumes an engine die or feeds back into a
 * result (THE GOVERNING RULE, D-EZ-104…111). The one engine call that wants a
 * `Dice` (the chip contest's odd/even die, which never changes who wins) is
 * handed a private throwaway.
 */

import { Dice } from '../../domain/dice.ts';
import {
  brakePedal,
  defenderSlot,
  resolveChipContest,
  type BrakeCalc,
} from '../../domain/challenge.ts';
import type { Car, Group, Quality } from '../../domain/types.ts';
import { RAIN_STOPS_ON } from '../../domain/turn.ts';
import { CHARTS, type ChartEntry } from '../../domain/repair.ts';
import { AW_AVOIDS_ON } from '../../domain/trouble.ts';
import { TWO_CHIP_BONUS, TWO_CHIP_THRESHOLD } from '../../domain/photoFinish.ts';

// ---------------------------------------------------------------------------
// Rationals and words
// ---------------------------------------------------------------------------

export interface Chance {
  /** Faces (or face combinations) on which it happens. */
  hits: number;
  /** All faces (or combinations). */
  of: number;
  /** hits / of. */
  p: number;
}

export const chance = (hits: number, of: number): Chance => ({ hits, of, p: of === 0 ? 0 : hits / of });

/** "50%", "certain", "no chance" — whole percent, never "0%" for a live chance. */
export function pct(c: Chance | number): string {
  const p = typeof c === 'number' ? c : c.p;
  if (p <= 0) return 'no chance';
  if (p >= 1) return 'certain';
  const n = Math.round(p * 100);
  if (n === 0) return 'under 1%';
  if (n === 100) return 'over 99%';
  return `${n}%`;
}

/** One die at or under `target`. */
export function dieAtMost(target: number, sides = 6): Chance {
  return chance(Math.max(0, Math.min(sides, Math.floor(target))), sides);
}

/** One die strictly over `target`. */
export function dieOver(target: number, sides = 6): Chance {
  const at = dieAtMost(target, sides);
  return chance(sides - at.hits, sides);
}

/** Two six-sided dice ADDED, at or under `target` (the 2d6 SUM reading). */
export function twoDiceAtMost(target: number): Chance {
  let hits = 0;
  for (let a = 1; a <= 6; a++) for (let b = 1; b <= 6; b++) if (a + b <= target) hits++;
  return chance(hits, 36);
}

/** "4 or less on the red die" in plain words, including the impossible and the certain. */
export function atMostWords(target: number, die: string, sides = 6): string {
  if (target >= sides) return `any ${die}`;
  if (target < 1) return `nothing on the ${die} will do`;
  if (target === 1) return `a 1 on the ${die}`;
  return `${target} or less on the ${die}`;
}

/** "8 or less on two dice". */
export function sumAtMostWords(target: number): string {
  if (target >= 12) return 'any two dice';
  if (target < 2) return 'nothing on two dice will do';
  if (target === 2) return 'snake eyes on two dice';
  return `${target} or less on two dice`;
}

// ---------------------------------------------------------------------------
// QUALITY CHALLENGE — RB/vi-vii, p.xiv, p.xvi (one red/white/blue throw)
// ---------------------------------------------------------------------------

export interface ChallengeOddsInput {
  /** The challenger as he stands before the throw. */
  challenger: Car;
  /** His spendable chips right now. */
  challengerChips: number;
  quality: Quality | 'TV';
  /** The group the challenge is aimed at ('leader' for a TOP-group challenge). */
  challengedGroup: Group;
  /** The car at a 1-based position, as the board stands before the throw. */
  carAt: (position: number) => Car | undefined;
  /** A defender's spendable chips right now. */
  chipsOf: (driverId: string) => number;
  /** HOUSE RULE D-EZ-86 clean-air brake symbols in force this turn (0 = none). */
  cleanAir: number;
  /** p.xiv solitaire chip auto-spend. */
  useChips: boolean;
  /** POST RACE INTERVIEW "AUTO WIN ONE QUALITY CHALLENGE", still unspent. */
  autoWin: boolean;
}

export interface ChallengeFace {
  /** Triples, the triangle on a red 6, or an empty slot — no challenge happens. */
  voided: boolean;
  /** Red at or under gas and blue outside the brake — before chips. */
  rolled: boolean;
  /** What stands after the grant and the chips. */
  success: boolean;
}

/** One red/white/blue throw through the printed rule. Mirrors challenge.ts `runOneChallenge`. */
export function challengeFace(input: ChallengeOddsInput, red: number, white: number, blue: number): ChallengeFace {
  const c = input.challenger;
  const none = { voided: true, rolled: false, success: false };
  // p.vi — triples void the challenge (a PROBLEM instead).
  if (red === white && white === blue) return none;
  // p.xvi — the triangle on a red 6.
  if (c.card.triangle && red === 6) return none;
  const slot = defenderSlot(input.challengedGroup, red, white);
  const defender = input.carAt(slot.position);
  if (!defender) return none;
  const brake = brakePedal(defender, slot.position, input.quality, input.cleanAir);
  const cannotDefend = defender.cannotRace === true;
  const defended = cannotDefend ? false : blue <= brake.effective;
  const rolled = red <= c.card.gasPedal && !defended;
  if (input.autoWin) return { voided: false, rolled, success: true };
  const contest = resolveChipContest({
    rawSuccess: rolled,
    challengerId: c.card.driverId,
    challengerChips: input.challengerChips,
    defenderId: defender.card.driverId,
    defenderChips: input.chipsOf(defender.card.driverId),
    dice: new Dice(1), // the odd/even die only decides who SPENDS, never who wins
    enabled: input.useChips,
    defenderMayDefend: !cannotDefend,
  });
  return { voided: false, rolled, success: contest.success };
}

export interface ChallengeOdds {
  /** The challenge stands (after grant and chips). */
  win: Chance;
  /** Won on the dice alone. */
  rolled: Chance;
  /** Red at or under the gas pedal. */
  gas: Chance;
  /** Triples or the triangle — something breaks instead. */
  breaks: Chance;
  /**
   * Against a single known defender (the race LEADER, white ignored): his brake
   * and the blue die's chance of beating it. Null when the white die picks.
   */
  versus: { defender: Car; brake: BrakeCalc; beatBrake: Chance } | null;
}

/** Every one of the 216 red/white/blue throws, through the printed rule. */
export function challengeOdds(input: ChallengeOddsInput): ChallengeOdds {
  let win = 0;
  let rolled = 0;
  let breaks = 0;
  for (let r = 1; r <= 6; r++)
    for (let w = 1; w <= 6; w++)
      for (let b = 1; b <= 6; b++) {
        const f = challengeFace(input, r, w, b);
        if (f.success) win++;
        if (f.rolled) rolled++;
        if (r === w && w === b) breaks++;
        else if (input.challenger.card.triangle && r === 6) breaks++;
      }
  let versus: ChallengeOdds['versus'] = null;
  if (input.challengedGroup === 'leader') {
    const defender = input.carAt(1);
    if (defender) {
      const brake = brakePedal(defender, 1, input.quality, input.cleanAir);
      versus = { defender, brake, beatBrake: defender.cannotRace ? chance(6, 6) : dieOver(brake.effective) };
    }
  }
  return {
    win: chance(win, 216),
    rolled: chance(rolled, 216),
    gas: dieAtMost(input.challenger.card.gasPedal),
    breaks: chance(breaks, 216),
    versus,
  };
}

// ---------------------------------------------------------------------------
// TROUBLE — the ESCAPE check, RB/p.v, p.xiv, EZRA-S2 §4.0
// ---------------------------------------------------------------------------

export interface EscapeOddsInput {
  /** The number the 2d6 SUM must be at or under (rating + track + trait + Chart C). */
  target: number;
  /** EZRA AW — a d6 of 6 saves a failed check. */
  awareness: boolean;
  /** Spendable chips: one saves a failed check (p.xiv). */
  chips: number;
  /** POST RACE INTERVIEW "AUTO ESCAPE ONCE", unspent. */
  autoEscape: boolean;
  /** RARE "BIG TROUBLE" — every check rolled twice; default 1. */
  rollsPerDriver?: number;
}

export interface EscapeOdds {
  /** The dice alone clear him. */
  dice: Chance;
  /** He is collected (after AW, chip and grant). */
  collected: Chance;
}

/** Mirrors trouble.ts `checkOneCar`: the sum(s), then AW, then the chip. */
export function escapeOdds(input: EscapeOddsInput): EscapeOdds {
  const rolls = Math.max(1, Math.trunc(input.rollsPerDriver ?? 1));
  // Every roll must be at or under (the worst of them decides).
  const one = twoDiceAtMost(input.target);
  const diceOf = 36 ** rolls;
  const diceHits = one.hits ** rolls;
  if (input.autoEscape) return { dice: chance(diceHits, diceOf), collected: chance(0, diceOf * 6) };
  const failHits = diceOf - diceHits;
  // Over failed sums × the AW die (always 6 faces in the space, so every case shares a denominator).
  let collected = failHits * 6;
  if (input.awareness) collected = failHits * (6 - 1); // a 6 drives clear
  if (AW_AVOIDS_ON !== 6) throw new Error('AW face changed — update escapeOdds');
  if (input.chips > 0) collected = 0;
  return { dice: chance(diceHits, diceOf), collected: chance(collected, diceOf * 6) };
}

// ---------------------------------------------------------------------------
// PHOTO FINISH ATTEMPT — EZRA Track Pack (gas pedal checks)
// ---------------------------------------------------------------------------

export interface PfaOdds {
  /** The attacker's red check (with the two-chip -1). */
  attack: Chance;
  /** The defender's red check holds him off. */
  hold: Chance;
  /** The cars swap. */
  pass: Chance;
  /** The effective target on the attacker's die. */
  attackTarget: number;
}

export function pfaOdds(attackerGas: number, chipsHeld: number, defenderGas: number): PfaOdds {
  const bonus = chipsHeld >= TWO_CHIP_THRESHOLD ? TWO_CHIP_BONUS : 0;
  let attack = 0;
  let pass = 0;
  for (let a = 1; a <= 6; a++) {
    if (a + bonus > attackerGas) continue;
    attack++;
    for (let d = 1; d <= 6; d++) if (d > defenderGas) pass++;
  }
  return {
    attack: chance(attack, 6),
    hold: dieAtMost(defenderGas),
    pass: chance(pass, 36),
    attackTarget: attackerGas - bonus,
  };
}

// ---------------------------------------------------------------------------
// PIT TURN LEAD CHALLENGE — RB/xiii-xiv(4): gas pedal, no defence, leader's chip
// ---------------------------------------------------------------------------

export function pitLeadOdds(challengerGas: number, leaderHasChip: boolean, leaderSpendsChip = true): { gas: Chance; lead: Chance } {
  const gas = dieAtMost(challengerGas);
  return { gas, lead: leaderHasChip && leaderSpendsChip ? chance(0, 6) : gas };
}

// ---------------------------------------------------------------------------
// RAIN THREAT — RARE RESULTS sheet: "a 1 or 6"
// ---------------------------------------------------------------------------

export function rainOdds(): Chance {
  let hits = 0;
  for (let f = 1; f <= 6; f++) if (RAIN_STOPS_ON.includes(f)) hits++;
  return chance(hits, 6);
}

// ---------------------------------------------------------------------------
// RARE RESULTS — the 11-66 chart, read as bands of what can happen
// ---------------------------------------------------------------------------

export type RareBand = 'surge-lead' | 'surge' | 'trouble' | 'penalty' | 'rain' | 'track' | 'crew' | 'super-rare' | 'incident' | 'other';

export const RARE_BAND_WORDS: Record<RareBand, string> = {
  'surge-lead': 'a car surges into the lead',
  surge: 'a car surges up a group',
  trouble: 'trouble — a wreck',
  penalty: 'a black flag or a penalty to the back',
  rain: 'rain threatens',
  track: 'the track goes off and everyone loses a quality',
  crew: 'a pit crew is hurt',
  'super-rare': 'the SUPER RARE chart',
  incident: 'a driver incident',
  other: 'something else on the sheet',
};

export function rareBandOf(entry: ChartEntry): RareBand {
  const types = (entry.effects ?? []).map((e) => (e as { type: string }).type);
  if (types.includes('surge')) {
    return (entry.effects ?? []).some((e) => (e as { type: string; to?: string }).type === 'surge' && (e as { to?: string }).to === 'LEAD') ? 'surge-lead' : 'surge';
  }
  if (types.some((t) => t === 'troubleEvent' || t === 'bigTrouble')) return 'trouble';
  if (types.some((t) => t === 'blackFlag' || t === 'moveToEndOfBottom')) return 'penalty';
  if (types.includes('rainThreat')) return 'rain';
  if (types.includes('allDriversLoseQuality')) return 'track';
  if (types.includes('lowerPitCrewGrade')) return 'crew';
  if (types.some((t) => t === 'rerollOn') && (entry.effects ?? []).some((e) => (e as { chart?: string }).chart === 'super-rare-results')) return 'super-rare';
  if (types.includes('driverIncident')) return 'incident';
  return 'other';
}

/** Each band's share of the 36 codes on the group's RARE RESULTS chart, most likely first. */
export function rareBands(group: 'top' | 'middle' | 'bottom'): { band: RareBand; chance: Chance }[] {
  const chart = CHARTS[`rare-results-${group}` as const] as { entries: Record<string, ChartEntry> };
  const counts = new Map<RareBand, number>();
  for (let r = 1; r <= 6; r++)
    for (let w = 1; w <= 6; w++) {
      const entry = chart.entries[`${r}${w}`];
      const band = entry ? rareBandOf(entry) : 'other';
      counts.set(band, (counts.get(band) ?? 0) + 1);
    }
  return [...counts]
    .map(([band, n]) => ({ band, chance: chance(n, 36) }))
    .sort((a, b) => b.chance.hits - a.chance.hits || a.band.localeCompare(b.band));
}

/** "4 in 36". */
export const inN = (c: Chance): string => `${c.hits} in ${c.of}`;
